import { t } from "@lingui/macro";
import { add, format, parse } from "date-fns";
import React, { useEffect, useState } from "react";

import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { VStack, HStack } from "@whoosh/design-system/src/components/primitive";
import { NumericalDayOfWeek } from "@whoosh/utilities/src/calendars";

import { Menu } from "../../../../overlay/Menu";
import { MenuContent } from "../../../../overlay/Menu/components/MenuContent";
import { Button } from "../../../Button/Button";
import { DateRangeOption } from "./DateRangeOption";
import { DateRangeTrigger } from "./DateRangeTrigger";
import { TwoCalendarMonths } from "./TwoCalendarMonths";
import { DateOption } from "./types";
import { checkWhichQuickDateResponseIsSelected } from "./utils/checkWhichQuickDateResponseIsSelected";
import { getQuickDateResponse } from "./utils/getQuickDateResponse";

type DateRangeOptions = {
  quickSelectionOption: DateOption;
  label: string;
}[];

type DateRangePickerProps = {
  startDate: string;
  endDate: string;
  onChange: (args: { startDate: string; endDate: string }) => void;
  fillEmptyDays?: boolean;
  clubWeekStartsOn: NumericalDayOfWeek;
};

export const DateRangePicker = ({
  startDate,
  endDate,
  onChange,
  fillEmptyDays = true,
  clubWeekStartsOn,
}: DateRangePickerProps) => {
  const [quickSelection, setQuickSelection] = useState<DateOption>(
    checkWhichQuickDateResponseIsSelected(startDate, endDate)
  );

  const [isOpen, setIsOpen] = useState(false);

  const [isCustomRangeOpen, setIsCustomRangeOpen] = useState(
    quickSelection === "Custom"
  );

  useEffect(() => {
    setIsCustomRangeOpen(quickSelection === "Custom");
  }, [quickSelection, isOpen]);

  const nowObj = new Date();
  const nextMonth = add(nowObj, { months: 1 });
  const nextMonthLabel = format(nextMonth, "MMMM");
  const secondMonth = add(nowObj, { months: 2 });
  const secondNextMonthLabel = format(secondMonth, "MMMM");

  const currentValueString =
    quickSelection === "Custom"
      ? `${format(
          parse(startDate, "yyyy-MM-dd", new Date()),
          "MMM do"
        )} to ${format(parse(endDate, "yyyy-MM-dd", new Date()), "MMM do")}`
      : quickSelection === "Next30"
      ? t`Next 30 days`
      : quickSelection === "NextMonth"
      ? nextMonthLabel
      : quickSelection === "SecondNextMonth"
      ? secondNextMonthLabel
      : quickSelection === "Previous30"
      ? t`Previous 30 days`
      : quickSelection;

  const handleUpdateRange = (startDate: string, endDate: string) => {
    setQuickSelection("Custom");
    onChange({ startDate, endDate });
    setIsCustomRangeOpen(false);
    setIsOpen(false);
  };

  const dateRangeOptions: DateRangeOptions = [
    { quickSelectionOption: "Next30", label: t`Next 30 days` },
    { quickSelectionOption: "Previous30", label: t`Previous 30 days` },
    { quickSelectionOption: "NextMonth", label: nextMonthLabel },
    { quickSelectionOption: "SecondNextMonth", label: secondNextMonthLabel },
    { quickSelectionOption: "Custom", label: t`Custom Range` },
  ];

  return (
    <Menu
      openPosition="bottom-start"
      overrideIsOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
      trigger={() => (
        <DateRangeTrigger
          setIsOpen={setIsOpen}
          isOpen={isOpen}
          currentValueString={currentValueString}
        />
      )}
      content={({ triggerWidth }) => {
        const isMobileLayout = triggerWidth < 880;
        return (
          <MenuContent
            size={isMobileLayout ? "matchTrigger" : "jumbotron"}
            maxHeight={"100%"}
            testID={"date-range-picker-content-container"}
          >
            <HStack gap={4}>
              {isCustomRangeOpen && isMobileLayout ? null : (
                <VStack minWidth={240} flexBasis={isMobileLayout ? "100%" : 0}>
                  {dateRangeOptions.map((option) => (
                    <DateRangeOption
                      key={option.quickSelectionOption}
                      quickSelection={quickSelection}
                      quickSelectionOption={option.quickSelectionOption}
                      label={option.label}
                      onPress={() => {
                        setQuickSelection(option.quickSelectionOption);
                        if (option.quickSelectionOption === "Custom") {
                          setIsCustomRangeOpen(true);
                        } else {
                          onChange(
                            getQuickDateResponse(option.quickSelectionOption)
                          );
                          setIsOpen(false);
                        }
                      }}
                    />
                  ))}
                </VStack>
              )}
              {isCustomRangeOpen || !isMobileLayout ? (
                <VStack
                  flex={isMobileLayout ? undefined : 1}
                  width={isMobileLayout ? "100%" : undefined}
                  height={"auto"}
                  testID="custom-range-container"
                >
                  {isMobileLayout ? (
                    <HStack
                      borderBottomWidth={1}
                      paddingBottom={3}
                      paddingHorizontal={3}
                      borderColor={"surface-dividers"}
                      marginBottom={3}
                    >
                      <Button
                        onPress={() => {
                          setIsCustomRangeOpen(false);
                        }}
                        leftIcon={<ArrowLeftIcon />}
                        size={"small"}
                        variant={"white"}
                        width={157}
                        testID="return-to-quick-selection-menu-button"
                        accessibilityLabel={t`Return to quick selection menu button`}
                      >{t`Custom Range`}</Button>
                    </HStack>
                  ) : null}
                  <TwoCalendarMonths
                    showTwoCalendars={!isMobileLayout}
                    curStartDate={startDate}
                    curEndDate={endDate}
                    handleUpdateRange={handleUpdateRange}
                    fillEmptyDays={fillEmptyDays}
                    clubWeekStartsOn={clubWeekStartsOn}
                  />
                </VStack>
              ) : null}
            </HStack>
          </MenuContent>
        );
      }}
    />
  );
};
